import { useState, useContext, createContext } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, MessageCircle } from "lucide-react";
import { NAV, SITE } from "../../lib/config";
import { track } from "../../lib/analytics";

export const QuoteContext = createContext(() => {});
export const useQuote = () => useContext(QuoteContext);

export default function Header({ scrolled = false }) {
  const [open, setOpen] = useState(false);
  const openQuote = useQuote();
  const { pathname } = useLocation();

  return (
    <header
      data-testid="site-header"
      data-scrolled={scrolled ? "1" : "0"}
      className="fixed top-0 inset-x-0 z-50 bg-white/70 backdrop-blur-xl border-b border-[#E2E8E4]/60 supports-[backdrop-filter]:bg-white/60 transition-shadow duration-300 data-[scrolled=1]:shadow-sm"
    >
      <div className={`max-w-7xl mx-auto px-5 md:px-10 flex items-center justify-between transition-[height] duration-300 ${scrolled ? "h-[52px] sm:h-[70px]" : "h-[70px]"}`}>
        <Link to="/" data-testid="logo-link" className="flex items-center group">
          <img
            src="/logo-trim.png"
            alt="Chennai Packs — premium food packaging"
            className={`w-auto transition-[height] duration-300 group-hover:scale-[1.03] ${scrolled ? "h-8 sm:h-11" : "h-11"}`}
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase().replace(/ /g, "-")}`}
              className={`px-3.5 py-2 text-sm font-medium rounded-full transition-colors ${
                pathname === n.to
                  ? "text-[#1D6B2A] bg-[#F8FAF8]"
                  : "text-[#3A4D3E] hover:text-[#1D6B2A]"
              }`}
            >
              {n.label}
            </Link>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href={`tel:${SITE.phoneRaw}`}
            className="flex items-center gap-2 text-sm font-semibold text-[#0A1C0E] hover:text-[#1D6B2A] transition-colors"
            data-testid="header-phone"
          >
            <Phone size={15} /> {SITE.phone}
          </a>
          <button
            data-testid="header-quote-btn"
            onClick={openQuote}
            className="bg-[#1D6B2A] text-white rounded-full px-5 py-2.5 text-sm font-semibold hover:bg-[#154d1e] transition-colors focus:ring-4 focus:ring-[#4CAF50]/30"
          >
            Get a Quote
          </button>
        </div>

        <div className="lg:hidden flex items-center gap-1">
          <a
            href={`tel:+${SITE.phoneRaw}`}
            onClick={() => track("Header Call Shortcut")}
            data-testid="header-call-shortcut"
            aria-label="Call Chennai Packs"
            className={`grid place-items-center w-9 h-9 rounded-full bg-[#1D6B2A] text-white shadow-md shadow-[#1D6B2A]/25 transition-all duration-300 ${scrolled ? "opacity-100 scale-100" : "opacity-0 scale-75 pointer-events-none"}`}
          >
            <Phone size={16} />
          </a>
          <a
            href={`https://wa.me/${SITE.whatsapp}?text=${encodeURIComponent("Hi Chennai Packs, I'd like a quote for your packaging products.")}`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => track("Header WhatsApp Shortcut")}
            data-testid="header-wa-shortcut"
            aria-label="Chat on WhatsApp"
            className={`grid place-items-center w-9 h-9 rounded-full bg-[#25D366] text-white shadow-md shadow-[#25D366]/25 transition-all duration-300 ${scrolled ? "opacity-100 scale-100" : "opacity-0 scale-75 pointer-events-none"}`}
          >
            <MessageCircle size={17} className="fill-white" />
          </a>
          <button
            className="p-2 text-[#0A1C0E]"
            data-testid="mobile-menu-btn"
            onClick={() => setOpen((o) => !o)}
          >
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden overflow-hidden bg-white border-t border-[#E2E8E4]"
          >
            <div className="px-5 py-4 flex flex-col gap-1">
              {NAV.map((n) => (
                <Link
                  key={n.to}
                  to={n.to}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${n.label.toLowerCase().replace(/ /g, "-")}`}
                  className="px-3 py-2.5 rounded-lg text-[#3A4D3E] font-medium hover:bg-[#F8FAF8]"
                >
                  {n.label}
                </Link>
              ))}
              <button
                onClick={() => {
                  setOpen(false);
                  openQuote();
                }}
                data-testid="mobile-quote-btn"
                className="mt-2 bg-[#1D6B2A] text-white rounded-full px-5 py-3 font-semibold"
              >
                Get a Quote
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
